/* eslint-disable react/no-children-prop */

import {
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  InputLeftElement,
  InputRightElement
} from "@chakra-ui/react";
import { RiSearchLine } from "react-icons/ri";
import { useFormContext } from "react-hook-form";

import {
  InputSearch,
  InputSearchProps
} from "components/Atomos/Form/Elements/InputSearch";
import { FilterPopover } from "./FilterPopover";
import { FieldValues } from "react-hook-form/dist/types";
import { ComponentType, useEffect, useRef, useState } from "react";
import { ResultArea } from "./ResultArea";
import { ListChildComponentProps } from "react-window";

type Props<T = any> = {
  onSubmit: (value: FieldValues) => void;
  onClean?: () => void;
  children?: React.ReactNode;
  isLoadingResult?: boolean;
  isFilterDisabled?: boolean;
  inputSearchPlaceholder?: string;
  itemsResult: T[];
  render: ComponentType<ListChildComponentProps<T>>;
  _inputProps?: InputSearchProps & React.RefAttributes<HTMLInputElement>;
};

export const InputSearchFilter = ({
  onSubmit,
  onClean,
  itemsResult,
  isLoadingResult = false,
  isFilterDisabled = false,
  inputSearchPlaceholder = "Pesquise",
  render,
  children,
  _inputProps
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [filterSubmited, setFilterSubmited] = useState<FieldValues>({});
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const { handleSubmit, reset, watch } = useFormContext();

  const values = watch();
  const arrValues = Object.values(values);
  const hasFilter =
    arrValues.filter((value) => value != "" && value != null).length > 0;

  const arrValuesSubmited = Object.values(filterSubmited);
  const qtdFilterSubmited = arrValuesSubmited.filter(
    (value) => value != "" && value != null
  ).length;
  const hasFilterSubmited = qtdFilterSubmited > 0;

  const handleFormSubmit = (formValues: FieldValues) => {
    setFilterSubmited(formValues);
    setIsFocused(true);
    onSubmit({ ...formValues, search: searchValue });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSubmit({ ...filterSubmited, search: value });
  };

  const handleClean = () => {
    setFilterSubmited({});
    reset();
    if (onClean) onClean();
  };

  const handleCleanSearch = () => {
    setSearch("");
    setSearchValue("");
    handleClean();
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement, Element>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    if (isFocused) {
      inputRef.current?.focus();
    }
  }, [isFocused]);

  useEffect(() => {
    if (!hasFilterSubmited && searchValue == "") {
      if (onClean) onClean();
    }
  }, [hasFilterSubmited, searchValue]);

  return (
    <Box onBlur={handleBlur} position="relative">
      <Flex minW={360}>
        <InputGroup>
          <InputLeftElement color="gray.500" pointerEvents="none">
            <RiSearchLine />
          </InputLeftElement>
          <InputSearch
            ref={inputRef}
            pr={children ? "8rem" : "6rem"}
            color="secondary"
            type="text"
            placeholder={inputSearchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onChangeValue={(value) => handleSearchChange(value)}
            onFocus={() => {
              setIsFocused(true);
            }}
            {..._inputProps}
          />
          <InputRightElement w="auto" mr="2">
            <HStack spacing={1}>
              {(!!search || hasFilterSubmited || itemsResult.length > 0) && (
                <Button
                  minW="16"
                  size="sm"
                  fontWeight="medium"
                  mr="1"
                  onClick={handleCleanSearch}
                >
                  Limpar
                </Button>
              )}
              {children && (
                <FilterPopover
                  qtdFiltersActive={qtdFilterSubmited}
                  isFilterDisabled={isFilterDisabled}
                >
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit(handleFormSubmit)(e);
                    }}
                  >
                    {children}
                    <Flex mt="4" justifyContent="space-between">
                      <Button type="submit" variant="main" size="sm">
                        Filtrar
                      </Button>
                      {hasFilter && (
                        <Button
                          variant="mainOutline"
                          size="sm"
                          onClick={handleClean}
                        >
                          Limpar
                        </Button>
                      )}
                    </Flex>
                  </form>
                </FilterPopover>
              )}
            </HStack>
          </InputRightElement>
        </InputGroup>
      </Flex>
      <ResultArea
        shouldOpen={isFocused}
        isLoading={isLoadingResult}
        items={itemsResult}
        render={render}
        isDirty={!!searchValue || hasFilterSubmited}
      />
    </Box>
  );
};
export function InputSearchFilterControl({ ...rest }: Props) {
  return (
    <Box
      className="leaflet-control"
      ml="0px !important"
      pl="10px"
      zIndex={1000}
    >
      <InputSearchFilter {...rest} />
    </Box>
  );
}
